/**
 * @module app.um
 */

import { autoinject } from 'aurelia-framework';
import { AuthPageContext } from 'app/um/lib/auth-page-context';
import { AuthService } from 'app/um/services/auth-service';
import { AuthWithLocalState } from 'app/um/models/local-state-extensions';
import { ComponentBase, IForm, StandardLoadOptions } from 'app/shared/lib/essette-site';
import { EventAggregator } from 'aurelia-event-aggregator';
import { CustomFieldService } from 'app/core/services/custom-field-service';
import { mergeObjects, deepClone } from 'app/shared/lib/utilities';
import { bindable } from 'aurelia-typed-observable-plugin';
import { getCustomFieldDefinitionReferences } from 'app/core/models/essette-types';
import { getReference } from 'app/shared/models/essette-types';
import { AuthHeaderChangeEvent } from 'app/um/lib/events';
import { authIsReadOnly } from 'app/um/lib/utilities';
import { Toaster } from 'app/shared/lib/toaster';
import { apiCall } from 'app/shared/lib/essette-api';
import * as UM from 'app/um/models/essette-api'
import * as Core from 'app/core/models/essette-api';

@autoinject()
export class SaveAuth extends ComponentBase implements IForm {

    ////****************** Fields and Properties **************************************////
    @bindable.objectNull({ changeHandler: 'loadAuth' })
    public auth: UM.Auth | null = null;

    @bindable.boolean
    public fromAuthSearch: boolean = false;

    protected editedAuth: UM.Auth | null = null;

    protected subClasses: UM.AuthSubClass[] = [];
    protected types: UM.AuthType[] = [];
    protected priorities: UM.AuthPriority[] = [];
    protected customFieldTarget: Core.CustomFieldTarget | null = null;

    protected readonly: boolean = false;
    protected hasEditAuthDetails: boolean = false;
    protected otherReferenceNumberFieldReadonly = false;

    ////****************** Element References ***************************************////


    ////****************** Lifecycle events (in order of execution) ******************////
    constructor(
        private readonly events: EventAggregator,
        private readonly authService: AuthService,
        private readonly customFieldService: CustomFieldService,
        private readonly context: AuthPageContext,
        private readonly toaster: Toaster,
        ...rest: any[]) {

        super(...rest);
    }

    protected bind(): void {
        this.checkPermissions();
        this.loadLookups();
        this.loadAuth();
    }

    ////****************** Events ***********************************************////


    ////****************** Functions ***********************************************////
    public configureAuth(newAuth?: UM.Auth): void {
        if(newAuth) this.auth = newAuth;
        if(this.auth?.id) this.context.configure(this.auth.id);
    }

    public clearAuthContext(): void {
        this.context.clear();
    }

    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        this.hasEditAuthDetails = this.accountContext.hasPermission('UM.Auth.Update.EditAuthDetails');
        const hasEditClosedAuth = this.accountContext.hasPermission('UM.Auth.Duty.EditClosedAuth');
        this.otherReferenceNumberFieldReadonly = this.env.config.pageConfigurations?.um?.auth?.modals?.editAuth?.otherReferenceNumberFieldReadonly || false;

        if(!this.auth) return;
        const hasEditAuth = this.accountContext.hasPermission('UM.Auth.Duty.EditAuth');
        this.readonly = authIsReadOnly(this.auth, this.env.user.id, hasEditAuth, hasEditClosedAuth);
    }

    private async loadAuth(): Promise<void> {
        if(!this.auth) return;
        const previousClassId = this.editedAuth?.class?.id;
        this.editedAuth = deepClone(this.auth);
        const currentClassId = this.editedAuth?.class?.id;
        // only load new subclasses if the class has changed
        if(previousClassId !== currentClassId) await this.loadAuthSubClassesByClass();
        // load custom field target if not found on context
        if(this.editedAuth.custom) {
            if(this.context.customFieldTarget) {
                this.customFieldTarget = this.context.customFieldTarget;
            } else {
                await this.loadCustomFieldTargets();
            }
            if(this.customFieldTarget) {
                this.editedAuth.custom.target = deepClone(this.customFieldTarget);
            }
        }
    }

    private refreshAuth() {
        if(!this.editedAuth || !this.auth) return;
        this.auth = mergeObjects(this.auth, this.editedAuth);
    }

    ////****************** IForm ****************************************************////
    public hasChanges(): boolean {
        throw new Error('Method not implemented.');
    }

    public submittedChanges?: any;

    public async submit(): Promise<boolean> {
        return await this.editAuth();
    }

    public reset(): void {
        if(!this.auth) return;
        this.editedAuth = deepClone(this.auth);
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadCustomFieldTargets(): Promise<void> {
        if(!this.auth || this.customFieldTarget) return;

        try {
            this.state.beginLoad();

            const includes = [
                'definitions',
                'definitions.choices'
            ];

            const fields = [
                'definitions.choices.description',
                'definitions.description',
                'definitions.property.field',
                'definitions.property.type'
            ];

            const customFieldTargets = await this.customFieldService.loadCustomFieldTargets({ targetType: 'UM.Auth' }, { top: 1 }, includes, fields);
            this.customFieldTarget = customFieldTargets.results[0] || null;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async editAuth(): Promise<boolean> {
        if(!this.editedAuth?.id) return false;

        let isSuccessful = false;
        try {
            this.state.beginSave();

            const saveAuth: UM.Auth = {
                id: this.editedAuth.id,
                class: {
                    subClass: getReference(this.editedAuth.class?.subClass)
                },
                type: getReference(this.editedAuth.type),
                priority: getReference(this.editedAuth.priority),
                alternateNumber: this.editedAuth.alternateNumber,
                custom: this.editedAuth.custom ? getCustomFieldDefinitionReferences(this.editedAuth.custom, this.customFieldTarget): null
            };

            const response = await this.authService.saveAuth(saveAuth);
            isSuccessful = response.isSuccessful;
            this.refreshAuth();
            await this.context.refreshAuthSettings(<AuthWithLocalState>this.auth);
            this.events.publish(new AuthHeaderChangeEvent());
            // Clear Auth context settings if edit form is for Auth Search
            if(this.fromAuthSearch) {
                this.context.clear();
            }
        } catch(error) {
            throw error;
        } finally {
            this.toaster.standard.saved('um:auth.$', isSuccessful);
            this.state.endSave();
            return isSuccessful;
        }
    }

    @apiCall
    private async loadLookups(): Promise<void> {
        try {
            this.state.beginSave();

            const options: StandardLoadOptions = {
                all: true
            };

            const fields = ['description'];

            const [responseTypes, responsePriority] = await Promise.all([
                this.authService.loadAuthTypes(true, options, undefined, fields),  //load all auth types, even IP only
                this.authService.loadAuthPriorities(options, undefined, fields)
            ]);

            this.types = responseTypes.results;
            this.priorities = responsePriority.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endSave();
        }
    }

    @apiCall
    private async loadAuthSubClassesByClass(): Promise<void> {
        const classId = this.auth?.class && this.auth.class.id;
        if(!classId) return;

        const options: StandardLoadOptions = {
            all: true
        };

        const fields = ['description'];

        try {
            this.state.beginSave();

            const response = await this.authService.loadAuthSubClassesByClass(classId, options, undefined, fields);
            this.subClasses = response.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endSave();
        }
    }
}

